// Milestone memory — Mochi's relationship anniversaries.
//
// Pure-function helpers + content definitions. Triggers + UI live in
// hooks/useMilestoneWatcher and components/MilestoneCelebration.
//
// Shame-safe rules baked in:
//   - Speech bubbles + chat messages NEVER reference task counts or misses.
//   - "Days since" is computed against the day the user FIRST opened the app
//     (`settings.first_active_date`). If they only used it once and came back
//     on day 100, the day-100 message still celebrates the return.

import api from "@/lib/api";
import { milestoneDay } from "@/lib/unlockPace";

const daysWord = (d) => (d === 1 ? "1 day" : `${d} days`);

// Each milestone defines its day-threshold, the post-celebration mood
// transition, the duration of the in-app celebration, and the rotating
// speech-bubble phrase bank. The chat-message text lives ONLY on the
// backend (services/milestone chat-seed endpoint) so we don't duplicate it.
export const MILESTONES = [
    {
        day: 7,
        name: "one week",
        initialMood: "excited",
        finalMood: null,                // stays in excited's natural ease-out
        celebrationMs: 3000,
        confettiVariant: "regular",
        unlock: null,
        phrases: [
            "hey… it's been a week. i really like our routine 🌸",
            "one week together. mochi is genuinely happy about this 🍡",
            "7 days. you came back every time. that means something.",
        ],
        betaPhrase: (d) => `${daysWord(d)} in. you came back every time. that means something 🌸`,
    },
    {
        day: 30,
        name: "one month",
        initialMood: "proud",
        finalMood: "happy",
        celebrationMs: 5000,
        confettiVariant: "regular",
        unlock: null,
        phrases: [
            "a whole month. mochi is so proud of us 🍡",
            "30 days. we've built something real here 🌸",
            "one month together. i've seen you do hard things. you're good at this.",
        ],
        betaPhrase: (d) => `${daysWord(d)}. we've built something real here 🌸`,
    },
    {
        day: 100,
        name: "one hundred days",
        initialMood: "party",
        finalMood: null,                // party is loud enough on its own
        celebrationMs: 6000,
        confettiVariant: "regular",
        phrases: [
            "100 days 👑 mochi has no words. only feelings.",
            "one hundred days together. i hope i've helped 🍡",
            "100. that's not an accident. that's you, showing up.",
        ],
        betaPhrase: (d) => `${daysWord(d)} 👑 mochi has no words. only feelings.`,
    },
    {
        day: 365,
        name: "one year",
        initialMood: "party",
        finalMood: "cozy",
        celebrationMs: 8000,
        confettiVariant: "gold",        // golden confetti
        phrases: [
            "a whole year 🎂 mochi has been with you through all of it.",
            "365 days. you kept coming back. every single time 🍡",
            "one year together. i know your patterns now. i know your hard days. i'm not going anywhere.",
        ],
        betaPhrase: (d) => `${daysWord(d)} 🎂 you kept coming back. every single time 🍡`,
    },
];

const MILESTONE_BY_DAY = Object.fromEntries(MILESTONES.map((m) => [m.day, m]));

// Day-count anchored at `first_active_date`. Returns null if no anchor yet
// (Mochi was just installed today — milestones can't fire on day 0).
export function daysSinceFirstOpen(settings) {
    if (!settings?.first_active_date) return null;
    try {
        // Both sides anchored to EST date strings so DST never adds/loses a day.
        const fmt = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/New_York",
            year: "numeric", month: "2-digit", day: "2-digit",
        });
        const today = fmt.format(new Date());
        const a = new Date(`${settings.first_active_date}T12:00:00Z`);
        const b = new Date(`${today}T12:00:00Z`);
        const ms = b.getTime() - a.getTime();
        return Math.floor(ms / 86_400_000);
    } catch {
        return null;
    }
}

// Returns the SINGLE pending milestone to surface this session, or null.
// Per the spec:
//   - If multiple are pending (e.g. user was away for 100+ days), show the
//     SMALLEST first. The next one will fire next open.
//   - A milestone is "pending" when daysSinceFirstOpen >= its EFFECTIVE day
//     (normal or compressed beta ladder) AND settings[`milestone_${day}_shown`]
//     is false. `m.day` stays the canonical id used for flags + API paths.
export function pickPendingMilestone(settings) {
    const days = daysSinceFirstOpen(settings);
    if (days == null) return null;
    for (const m of MILESTONES) {
        if (days >= milestoneDay(settings, m.day) && !settings[`milestone_${m.day}_shown`]) {
            return m;
        }
    }
    return null;
}

// On the compressed ladder the spec phrases ("a whole year…") would lie, so
// we use the milestone's day-count template instead.
export function pickMilestonePhrase(milestone, settings) {
    if (!milestone) return null;
    const effective = milestoneDay(settings, milestone.day);
    if (effective !== milestone.day && milestone.betaPhrase) return milestone.betaPhrase(effective);
    const pool = milestone.phrases || [];
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
}

// Mark a milestone as shown on the backend. Returns the updated Settings.
export async function markMilestoneSeen(day) {
    const res = await api.post(`/milestones/${day}/seen`);
    return res.data;
}

// Ask the backend to drop the Mochi-initiated chat message into history so
// the user finds it when they next open chat.
export async function seedMilestoneChat(day) {
    const res = await api.post(`/milestones/${day}/seed-chat`);
    return res.data;
}

// Used by accessory overlay: which accessory should currently render on
// Mochi? Returns "crown" | "birthday-hat" | null.
//
// Priority: birthday hat (rare, end-of-day window) wins over crown so the
// 365-day moment still feels special even if the crown is also equipped.
// The crown is a PERMANENT unlock once earned — kept as long as
// `crown_equipped` is true.
//
// User toggles are honoured here so the caller never has to layer them:
//   - Birthday hat respects the session-level `accessory_visible` flag.
//   - Crown is gated solely by the permanent `crown_equipped` flag.
export function activeAccessory(settings) {
    if (!settings) return null;
    const now = Date.now();
    const hat = settings.milestone_365_hat_until;
    const hatActive = hat && Date.parse(hat) > now;
    const hatVisible = settings.accessory_visible !== false;
    // Mochi BORROWS the party hat on the account's monthly anniversary (and
    // on the user's birthday while the claim is pending) even if it isn't
    // earned yet — the real unlock still has to be claimed.
    const occasion = settings.unlock_progress?.hat?.occasion;
    const borrowed = !!occasion && !settings.hat_unlocked;
    // Birthday hat wins (rare + celebratory) — legacy anniversary window,
    // the permanent `hat_equipped` flag, or today's occasion; all honour the
    // session-level `accessory_visible` toggle.
    if ((hatActive || settings.hat_equipped || borrowed) && hatVisible) return "birthday-hat";
    if (settings.crown_equipped) return "crown";
    return null;
}

export const __exports_for_tests = { MILESTONE_BY_DAY };
